import { useRollbar } from '@rollbar/react'
import { usePage as useTypedPage } from 'publisher/store'
import typedPageSelectors from 'publisher/store/page/pageSelectors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as exceptions from '../../common/errors'
import { optInFail } from '../actions/optInActions'
import { SHOW_TWO_STEP_PAYMENT_FORM_STEP_PAYMENT } from '../actionTypes'
import { twoStepOptIn } from '../api/payment'
import { Query, QueryKeys } from '../components/core/MatchMedia'
import { getDateTimeField, getFieldsBySlugs } from '../reducers/optInReducer'
import { getAscendantPopupId } from '../reducers/pageReducer'
import useManagement, { selectors as managementSel } from './useManagement'
import useOptIn from './useOptIn'
import usePage, { selectors as pageSelectors } from './usePage'
import usePayment, { selectors as paymentSelectors } from './usePayment'
import { useRecaptchaValidation } from './useRecaptchaValidation'

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

function useTwoStepOptInSubmit(optInStepEntity) {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const [isLoading, setLoading] = useState(false)
  const isTemplate = usePage(pageSelectors.isTemplate)
  const [errors, setErrors] = useState([])
  const pageId = usePage(pageSelectors.getPageId)
  const twoStepPaymentFormFieldsSlugs = usePage(page =>
    pageSelectors.findEntityDescendantsFieldsSlugs(page, optInStepEntity),
  )
  const companyNameField = usePage(pageSelectors.getCompanyNameField)
  const companyVatField = usePage(pageSelectors.getCompanyVatField)
  const isCustomerPersonal = usePayment(paymentSelectors.isCustomerPersonal)
  const fields = useOptIn(state =>
    getFieldsBySlugs(state, twoStepPaymentFormFieldsSlugs),
  )
  const dateTimeField = useOptIn(getDateTimeField)
  const popupId = usePage(page => getAscendantPopupId(page, optInStepEntity))
  const confirmedExplicitConsentsIds = useManagement(
    managementSel.getConfirmedExplicitConsentsIds,
  )

  const mandatoryExplicitConsents = useTypedPage(p =>
    typedPageSelectors.findEntityDescendantsCheckboxes(p, optInStepEntity.id),
  )

  const dispatch = useDispatch()

  const { validateRecaptchas } = useRecaptchaValidation(optInStepEntity)

  const submit = async e => {
    e.preventDefault()
    if (isTemplate) {
      return
    }

    const errors = mandatoryExplicitConsents.reduce(
      (errors, explicitConsent) => {
        if (confirmedExplicitConsentsIds.includes(explicitConsent.id)) {
          return errors
        }
        errors.push(
          'options' in explicitConsent
            ? explicitConsent.options.errorText
            : explicitConsent.errorText,
        )
        return errors
      },
      [],
    )

    if (errors.length > 0) {
      setErrors(errors)
      return
    }

    const { recaptchaToken, error } = validateRecaptchas()

    if (error) return

    setLoading(true)

    if (isCustomerPersonal) {
      if (companyNameField) {
        delete fields[companyNameField.options?.slug]
        delete fields[companyNameField.slug]
      }

      if (companyVatField) {
        delete fields[companyVatField.options?.slug]
        delete fields[companyVatField.slug]
      }
    }
    try {
      await twoStepOptIn(
        pageId,
        fields,
        dateTimeField,
        timeZone,
        popupId,
        window.matchMedia(Query[QueryKeys.DESKTOP]).matches,
        optInStepEntity.id,
        confirmedExplicitConsentsIds,
        recaptchaToken,
      )

      dispatch({
        type: SHOW_TWO_STEP_PAYMENT_FORM_STEP_PAYMENT,
      })

      setLoading(false)
    } catch (error) {
      if (error instanceof exceptions.BadRequest) {
        setErrors(error.response.data.errors.common)
        dispatch(optInFail({ fields: error.response.data.errors.fields }))
      } else if (error instanceof exceptions.ServiceUnavailable) {
        setErrors([t('core.errors.service_temporary_unavailable')])
      } else if (error instanceof exceptions.NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else if (error instanceof exceptions.NotFound) {
        setErrors([t('page Not Found')])
      } else {
        rollbar.error(`Opt-in error`, error)
      }
      setErrors(errors)
      setLoading(false)
    }
  }

  return [isLoading, errors, submit]
}

export default useTwoStepOptInSubmit
